<script setup lang="ts">
//#region imports
import { computed, nextTick, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { omit } from 'lodash-es';
import { usePermissions } from '@/composables/usePermissions';
import { useApi } from '@/api';
import { useUpdateUrl } from '@/composables/useUpdateUrl';
import { useTablePaginator, useTableSearch, useTableSort } from '@/composables/useTableLS';
import { useLodash } from '@/setup/lodash';
import { usePopupManager } from '@/components/PopupManager.vue';
import type { UiTableNewField, UiTableNewSortEmit } from '@/types/table';
import type { GetDomainsManagementPlatformBettingDomainsData, GetDomainsManagementPlatformBettingDomainsResponse } from '@/types/openapi-ts';
import type { SettingsPageQuery } from '@/types/core';
import type UiPagination from '@/components/UIElements/UiPagination.vue';
//#endregion

//#region types
type DedicatedSiteDomainsQuery = SettingsPageQuery<GetDomainsManagementPlatformBettingDomainsData['query']>;
//#endregion

//#region const
const LS_PATH = 'DedicatedSiteDomains';

const sizes = {
  1080: 20,
  1440: 30,
};

const fields: UiTableNewField<GetDomainsManagementPlatformBettingDomainsResponse['payload'][number]>[] = [
  {
    name: 'incrementing_id',
    sortable: 'custom',
    align: 'left',
    width: '60',
  },
  {
    name: 'domain_name',
    sortable: 'custom',
    minWidth: '100',
  },
  {
    name: 'affiliate_email',
    sortable: 'custom',
    computedValue: (row) => row.affiliate_email || '—',
    link: getAffiliateLink,
  },
  {
    name: 'affiliate_offer_name',
    computedValue: (row) => row.affiliate_offer_name || '—',
  },
  {
    name: 'created_at',
    sortable: 'custom',
    width: '130',
    format: 'date-time',
  },
];
//#endregion

//#region composables
const api = useApi();
const route = useRoute();
const updateUrl = useUpdateUrl();
const _ = useLodash();
const popupManager = usePopupManager();
const { t } = useI18n();
const { hasPermission } = usePermissions();
const { searchQuery } = useTableSearch();
const { limit, count, page, setPage } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});
const { sortProp, sortOrder, optionsSort } = useTableSort({
  initialOptions:{
    sort_column: 'incrementing_id',
    sort_dir: 'desc',
  },
  lsPath: LS_PATH,
  fields,
});
//#endregion

//#region state
const tableData = ref<GetDomainsManagementPlatformBettingDomainsResponse['payload']>([]);
const isDataLoading = ref(false);
const isFirstLoading = ref(true);
//#endregion

//#region computed
const canEditDedicatedDomains = computed(() => {
  return hasPermission('platform_betting_domains_edit');
});
//#endregion

//#region methods
function initData() {
  const _urlData = updateUrl.getParseParams(route.hash);

  if (!_.isEmpty(_urlData)) {
    setPage(_urlData);
    delete _urlData.page;
    delete _urlData.limit;

    _.merge(optionsSort.value, _urlData);
  }
  nextTick(() => {
    fetchData();
  });
}

function _query() {
  return {
    ...optionsSort.value,
    limit: limit.value,
    search: searchQuery.value,
    offset: limit.value * page.value - limit.value,
  } as GetDomainsManagementPlatformBettingDomainsData['query'];
}

function _completedFilterUrl() {
  const _dataFilters: DedicatedSiteDomainsQuery = omit(_query(), 'offset');
  _dataFilters.page = page.value;
  updateUrl.updateGetParams(_dataFilters);
}

async function fetchData(silent = false) {
  if (!silent && !isFirstLoading.value) {
    isDataLoading.value = true;
  }

  _completedFilterUrl();
  const options = _query();

  try {
    const { data: { payload, misc } } = await api.getPlatformBettingDomain(options);

    count.value = misc!.count;
    tableData.value = payload;
  } finally {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  }

  if(!isFirstLoading.value) {
    isDataLoading.value = false;
  } else {
    isFirstLoading.value = false;
  }
}

function handleRefresh () {
  page.value = 1;
  fetchData();
}

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

function createDedicatedDomains() {
  popupManager.createDedicatedSiteDomainsPopup.open({
    listeners: {
      success: () => {
        fetchData(true);
      },
    },
  });
}

function openPinAffiliatePopup(row: GetDomainsManagementPlatformBettingDomainsResponse['payload'][number]) {
  const { affiliate_email, affiliate_id, affiliate_offer_id, id } = row;
  let affiliate = null;
  if(affiliate_id) {
    affiliate = {
      affiliate_email,
      affiliate_id,
    };
  }
  popupManager.pinAffiliateToDomainPopup.open({
    data: {
      domainId: id,
      offer: affiliate_offer_id,
      affiliate,
    },
    listeners: {
      success: () => {
        fetchData(true);
      },
    },
  });
}

function getAffiliateLink(row: GetDomainsManagementPlatformBettingDomainsResponse['payload'][number]) {
  if (!row.affiliate_email || !hasPermission('affiliates_view')) {
    return '';
  }
  return `/affiliates/${row.affiliate_id}/info`;
}

function handleDelete(row: GetDomainsManagementPlatformBettingDomainsResponse['payload'][number]) {
  const confirmPopup = popupManager.confirm.open({
    data: {
      title: t('domainManagement.buttons.delete'),
      message: t('domainManagement.messages.delete', { name: row.domain_name}),
      bodyIcon: 'trash-alt',
      confirmIcon: 'trash-alt',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('domainManagement.buttons.delete'),
      confirmType: 'negative',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        try {
          await api.deletePlatformBettingDomain(row.id);
          await fetchData();
        } catch (error) {
          window.console.error(error);
        }
        context.handleClose();
      },
    },
  });
}
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <div class="dedicated-site-domains">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('domainManagement.pageTitle.dedicatedSiteDomains') }}
      </template>
      <template v-slot:left-slot>
        <UiInput
          v-model.trim="searchQuery"
          :width="180"
          is-search
          @search="handleRefresh"
        />
      </template>
      <template v-slot:right-slot>
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
      <template v-slot:actions>
        <UiButton
          v-if="canEditDedicatedDomains"
          filled
          icon="plus"
          @click="createDedicatedDomains"
        >
          {{ $t('domainManagement.buttons.add_dns_hosting_account') }}
        </UiButton>
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      is-static
      i18n-path="domainManagement"
      @sort="handleSort"
    >
      <template
        v-if="canEditDedicatedDomains"
        v-slot:append
      >
        <ElTableColumn
          align="center"
          width="76"
        >
          <template v-slot:header="scope">
            <div :key="scope.$index">
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <ActionIcon
              :icon="!!scope.row.affiliate_email ? 'pen' : 'link'"
              :tooltip="!!scope.row.affiliate_email
                ? $t('domainManagement.buttons.edit')
                : $t('domainManagement.popup.pinned')"
              @click="openPinAffiliatePopup(scope.row)"
            />
            <ActionIcon
              icon="trash-alt"
              color="var(--text-negative)"
              :tooltip="$t('domainManagement.buttons.delete')"
              :disabled="!!scope.row.affiliate_email"
              @click="handleDelete(scope.row)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>
